import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Receipt, Eye, Search, DollarSign, CheckCircle, Clock, AlertCircle, CreditCard, Plus, X, Trash2, Printer, FileDown, Factory, Truck, Package, MapPin, ClipboardList, Pencil, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { isFullAdmin } from '../tax/taxDisplay';
import { InvoiceFormDialog } from './invoices/InvoiceFormDialog';
import { postSend } from './invoices/invoiceApi';
import { isManual, ManualLineRows, DiscountRows, manualRowsHtml, TEST_LABEL } from './invoices/ManualLines';
import { InternalNotes } from './invoices/InternalNotes';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formatPrice, formatAddress } from '../../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { PermissionGate } from '../../context/PermissionContext';
import { TrackingModal } from './TrackingModal';
import { TaxExplainLine, TestBadge, NexusBanner } from '../tax/TaxBadges';
import { groupCartByRoom, roomGroupedRowsHtml } from '../../lib/cartGroups';
import { openInstallSheet } from '../../lib/installSheet';
import { openPackingSlip } from '../../lib/packingSlip';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const INVOICE_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-neutral-100 text-neutral-700', icon: Clock },
  { value: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Receipt },
  { value: 'partial', label: 'Partially Paid', color: 'bg-amber-100 text-amber-700', icon: DollarSign },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-neutral-100 text-neutral-500', icon: AlertCircle },
];

const PRODUCTION_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-neutral-100 text-neutral-600' },
  { value: 'queued', label: 'Queued', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_production', label: 'In Production', color: 'bg-amber-100 text-amber-700' },
  { value: 'quality_check', label: 'Quality Check', color: 'bg-purple-100 text-purple-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-red-100 text-red-700' },
];

const FULFILLMENT_STATUSES = {
  shipping: [
    { value: 'pending', label: 'Pending', color: 'bg-neutral-100 text-neutral-600' },
    { value: 'ready', label: 'Ready to Ship', color: 'bg-blue-100 text-blue-700' },
    { value: 'shipped', label: 'Shipped', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'in_transit', label: 'In Transit', color: 'bg-cyan-100 text-cyan-700' },
    { value: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-amber-100 text-amber-700' },
    { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700' },
  ],
  pickup: [
    { value: 'pending', label: 'Pending', color: 'bg-neutral-100 text-neutral-600' },
    { value: 'ready_for_pickup', label: 'Ready for Pickup', color: 'bg-blue-100 text-blue-700' },
    { value: 'picked_up', label: 'Picked Up', color: 'bg-green-100 text-green-700' },
  ],
  delivery: [
    { value: 'pending', label: 'Pending', color: 'bg-neutral-100 text-neutral-600' },
    { value: 'ready', label: 'Ready', color: 'bg-blue-100 text-blue-700' },
    { value: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-amber-100 text-amber-700' },
    { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700' },
  ],
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'ach', label: 'ACH' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'other', label: 'Other' },
];

// Company Information
const COMPANY_INFO = {
  name: 'Apollo Shades',
  address: '1 Brooklyn Navy Yard, Building 33, Suite 201B',
  city: 'Brooklyn',
  state: 'NY',
  zip: '11205',
  phone: '(212) 209-0654',
  email: 'info@apolloshades.com',
};

// Helper functions - defined outside component so modal can use them
const getStatusStyle = (status) => {
  const statusObj = INVOICE_STATUSES.find(s => s.value === status);
  return statusObj?.color || 'bg-neutral-100 text-neutral-700';
};

const getStatusLabel = (status) => {
  const statusObj = INVOICE_STATUSES.find(s => s.value === status);
  return statusObj?.label || status;
};

const getPaymentMethodLabel = (method) => {
  return PAYMENT_METHODS.find(m => m.value === method)?.label || method;
};

export const InvoicesTab = ({ token }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingInvoice, setTrackingInvoice] = useState(null);
  const [formInvoice, setFormInvoice] = useState(null);
  const { user: authUser } = useAuth();
  const admin = isFullAdmin(authUser);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      await axios.put(`${API_URL}/admin/invoices/${invoiceId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoices(invoices.map(inv => inv.id === invoiceId ? { ...inv, status } : inv));
      toast.success('Invoice status updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Production status update
  const updateProductionStatus = async (invoiceId, status, notes = '') => {
    try {
      const response = await axios.put(`${API_URL}/admin/invoices/${invoiceId}/production`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoices(invoices.map(inv => inv.id === invoiceId ? { ...inv, production_status: status } : inv));
      toast.success(`Production status updated to ${PRODUCTION_STATUSES.find(s => s.value === status)?.label || status}`);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update production status');
    }
  };

  // Fulfillment status update
  const updateFulfillmentStatus = async (invoiceId, statusData) => {
    try {
      const response = await axios.put(`${API_URL}/admin/invoices/${invoiceId}/fulfillment`,
        statusData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoices(invoices.map(inv => inv.id === invoiceId ? { ...inv, fulfillment_status: statusData.status } : inv));
      toast.success(`Fulfillment status updated to ${statusData.status.replace(/_/g, ' ')}`);
      fetchInvoices();
      setShowTrackingModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fulfillment status');
    }
  };

  const handlePaymentAdded = async (invoiceId) => {
    fetchInvoices();
    try {
      const r = await axios.get(`${API_URL}/admin/invoices/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedInvoice(r.data);
    } catch (e) { /* list refresh still shows the new state */ }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(invoices.filter(i => i.id !== invoiceId));
      toast.success('Invoice deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (statusFilter !== 'all' && invoice.status !== statusFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        invoice.customer_name?.toLowerCase().includes(search) ||
        invoice.customer_email?.toLowerCase().includes(search) ||
        invoice.invoice_number?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const statusCounts = {
    all: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
  };

  // TEST records never count toward sales totals
  const realInvoices = invoices.filter(i => !i.is_test);
  const totalReceivable = realInvoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled')
    .reduce((sum, i) => sum + (i.balance_due ?? i.total ?? 0), 0);
  const totalPaid = realInvoices.reduce((sum, i) => sum + (i.amount_paid || 0), 0);

  const handlePrintInvoice = (invoice) => {
    const printWindow = window.open('', '_blank');
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .header h1 { margin: 0; font-size: 28px; }
          .header p { margin: 5px 0; color: #666; }
          .invoice-title { text-align: center; font-size: 24px; margin: 20px 0; }
          .invoice-title span { background: ${invoice.status === 'paid' ? '#dcfce7' : '#fef3c7'}; padding: 5px 15px; border-radius: 20px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
          .info-box h3 { margin: 0 0 10px 0; font-size: 14px; color: #666; text-transform: uppercase; }
          .info-box p { margin: 3px 0; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #f5f5f5; font-weight: 600; }
          .totals { text-align: right; }
          .totals table { width: 300px; margin-left: auto; }
          .totals td { border: none; padding: 5px 10px; }
          .total-row { font-weight: bold; font-size: 18px; border-top: 2px solid #333 !important; }
          .paid-stamp { text-align: center; padding: 20px; margin: 20px 0; }
          .paid-stamp span { background: #22c55e; color: white; font-size: 24px; font-weight: bold; padding: 10px 30px; border-radius: 5px; }
          .footer { margin-top: 50px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>APOLLO SHADES</h1>
          <p>1 Brooklyn Navy Yard, Building 33, Suite 201B, Brooklyn, NY 11205</p>
          <p>(212) 209-0654 | info@apolloshades.com</p>
        </div>
        
        <div class="invoice-title">
          <span>INVOICE ${invoice.invoice_number}</span>
        </div>
        <p style="text-align: center; color: #666;">Date: ${new Date(invoice.created_at).toLocaleDateString()} | Due: ${invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'Upon Receipt'}</p>
        
        ${invoice.is_test ? `<div style="text-align:center;margin:12px 0;padding:8px;border:2px solid #dc2626;color:#dc2626;font-weight:bold;">${TEST_LABEL}</div>` : ''}
        ${invoice.status === 'paid' ? '<div class="paid-stamp"><span>PAID IN FULL</span></div>' : ''}
        
        <div class="info-grid">
          <div class="info-box">
            <h3>Bill To</h3>
            <p><strong>${invoice.customer_name}</strong></p>
            ${invoice.company_name ? `<p>${invoice.company_name}</p>` : ''}
            <p>${invoice.customer_email}</p>
          </div>
          <div class="info-box">
            <h3>Invoice Details</h3>
            <p><strong>Status:</strong> ${invoice.status?.toUpperCase()}</p>
            <p><strong>Items:</strong> ${invoice.items?.length || 0}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>${isManual(invoice) ? '' : 'Dimensions'}</th>
              <th>Qty</th>
              <th style="text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${isManual(invoice) ? manualRowsHtml(invoice.items) : roomGroupedRowsHtml(invoice.items)}
          </tbody>
        </table>
        
        <div class="totals">
          <table>
            ${(invoice.discount_total || 0) > 0 ? `<tr><td>Items</td><td>$${(invoice.gross_subtotal || 0).toFixed(2)}</td></tr><tr><td>Discounts</td><td>-$${invoice.discount_total.toFixed(2)}</td></tr>` : ''}
            <tr><td>Subtotal</td><td>$${(invoice.subtotal || 0).toFixed(2)}</td></tr>
            <tr><td>Tax</td><td>$${(invoice.tax || 0).toFixed(2)}</td></tr>
            <tr class="total-row"><td>Total</td><td>$${(invoice.total || 0).toFixed(2)}</td></tr>
            ${(invoice.amount_paid || 0) > 0 ? `<tr style="color: green;"><td>Paid</td><td>-$${(invoice.amount_paid || 0).toFixed(2)}</td></tr>` : ''}
            ${(invoice.balance_due || 0) > 0 ? `<tr style="color: red; font-weight: bold;"><td>Balance Due</td><td>$${(invoice.balance_due || 0).toFixed(2)}</td></tr>` : ''}
          </table>
        </div>
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>Questions? Contact us at (212) 209-0654 or info@apolloshades.com</p>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">Invoices</h3>
          <p className="text-sm text-neutral-500">
            {realInvoices.length} invoices • {formatPrice(totalReceivable)} receivable{invoices.length > realInvoices.length ? ` • ${invoices.length - realInvoices.length} TEST not counted` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {admin && <Button onClick={() => setFormInvoice('new')} size="sm" data-testid="create-invoice-btn"><Plus className="w-4 h-4 mr-1" /> Create Invoice</Button>}
          <Button onClick={fetchInvoices} variant="outline" size="sm">
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
          <div className="text-2xl font-bold text-neutral-900" data-testid="invoices-total-count">{realInvoices.length}</div>
          <div className="text-sm text-neutral-500">Total Invoices</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
          <div className="text-2xl font-bold text-amber-600" data-testid="invoices-outstanding">{formatPrice(totalReceivable)}</div>
          <div className="text-sm text-amber-700">Outstanding</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-600" data-testid="invoices-collected">{formatPrice(totalPaid)}</div>
          <div className="text-sm text-green-700">Collected</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="text-2xl font-bold text-red-600">{statusCounts.overdue}</div>
          <div className="text-sm text-red-700">Overdue</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2 flex-wrap">
          {['all', 'draft', 'sent', 'partial', 'paid', 'overdue'].map(status => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
            >
              {status === 'all' ? 'All' : getStatusLabel(status)} ({statusCounts[status] || 0})
            </Button>
          ))}
        </div>
      </div>

      {/* Invoices List */}
      <div className="space-y-3">
        {filteredInvoices.length === 0 ? (
          <div className="text-center py-12 text-neutral-500">
            <Receipt className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No invoices found</p>
            <p className="text-sm mt-2">{admin ? 'Use Create Invoice, or convert a quote from the Quotes tab' : 'Convert quotes to invoices from the Quotes tab'}</p>
          </div>
        ) : (
          filteredInvoices.map((invoice) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`border rounded-lg p-4 bg-white hover:border-amber-300 transition-colors ${
                invoice.status === 'overdue' ? 'border-red-300' : 'border-neutral-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    invoice.status === 'paid' ? 'bg-green-100' :
                    invoice.status === 'overdue' ? 'bg-red-100' : 'bg-blue-100'
                  }`}>
                    <Receipt className={`w-5 h-5 ${
                      invoice.status === 'paid' ? 'text-green-600' :
                      invoice.status === 'overdue' ? 'text-red-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">{invoice.invoice_number}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusStyle(invoice.status)}`}>
                        {getStatusLabel(invoice.status)}
                      </span>
                      <TestBadge doc={invoice} testId={`invoice-test-badge-${invoice.invoice_number}`} />
                    </div>
                    <TaxExplainLine doc={invoice} testId={`invoice-tax-explain-${invoice.invoice_number}`} />
                    <p className="text-sm text-neutral-600">
                      {invoice.customer_name} {invoice.company_name && `• ${invoice.company_name}`}
                    </p>
                    <p className="text-xs text-neutral-500">
                      Created: {new Date(invoice.created_at).toLocaleDateString()}
                      {invoice.due_date && ` • Due: ${new Date(invoice.due_date).toLocaleDateString()}`}
                    </p>
                    
                    {/* Production & Fulfillment Status Badges */}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {/* Production Status */}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        PRODUCTION_STATUSES.find(s => s.value === invoice.production_status)?.color || 'bg-neutral-100 text-neutral-600'
                      }`}>
                        <Factory className="w-3 h-3" />
                        {PRODUCTION_STATUSES.find(s => s.value === invoice.production_status)?.label || 'Pending'}
                      </span>
                      
                      {/* Fulfillment Status */}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        (FULFILLMENT_STATUSES[invoice.fulfillment_type || 'shipping']?.find(s => s.value === invoice.fulfillment_status)?.color) || 'bg-neutral-100 text-neutral-600'
                      }`}>
                        {invoice.fulfillment_type === 'pickup' ? <MapPin className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                        {FULFILLMENT_STATUSES[invoice.fulfillment_type || 'shipping']?.find(s => s.value === invoice.fulfillment_status)?.label || 'Pending'}
                      </span>
                      
                      {/* Tracking Number */}
                      {invoice.tracking_number && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                          📦 {invoice.tracking_number}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-mono font-medium text-lg">{formatPrice(invoice.total)}</p>
                    {invoice.balance_due > 0 && invoice.balance_due !== invoice.total && (
                      <p className="text-xs text-amber-600">Balance: {formatPrice(invoice.balance_due)}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {['paid', 'partial'].includes(invoice.status) || (invoice.amount_paid || 0) > 0 || (!admin && invoice.status === 'cancelled') ? (
                      <span className="text-xs text-neutral-500 px-2" title={invoice.status === 'cancelled' ? 'Only Admins can reopen' : 'Set by recorded payments'} data-testid={`invoice-status-locked-${invoice.id}`}>{invoice.status === 'cancelled' && !(invoice.amount_paid > 0) ? 'Cancelled' : 'Set by payments'}</span>
                    ) : (
                      <select
                        value={invoice.status}
                        onChange={(e) => updateInvoiceStatus(invoice.id, e.target.value)}
                        className="text-sm border border-neutral-200 rounded px-2 py-1"
                        data-testid={`invoice-status-select-${invoice.id}`}
                      >
                        {INVOICE_STATUSES.filter(s => !['paid', 'partial'].includes(s.value) && (admin || s.value !== 'cancelled' || invoice.status === 'cancelled')).map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                        {!INVOICE_STATUSES.some(s => s.value === invoice.status) && <option value={invoice.status}>{invoice.status}</option>}
                      </select>
                    )}
                    {admin && invoice.status !== 'cancelled' && !(invoice.amount_paid > 0) && !(invoice.payments || []).length && (
                      <Button variant="outline" size="sm" onClick={() => setFormInvoice(invoice)} title="Edit invoice" data-testid={`invoice-edit-${invoice.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {admin && !['cancelled', 'paid'].includes(invoice.status) && invoice.customer_email && (
                      <Button variant="outline" size="sm" onClick={async () => { if (await postSend(token, invoice.id)) fetchInvoices(); }} title="Email invoice to customer" data-testid={`invoice-send-${invoice.id}`}>
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`invoice-view-${invoice.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Opening invoice:', invoice.id);
                        setSelectedInvoice(invoice);
                      }}
                      title="View Invoice"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {/* Print Invoice */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handlePrintInvoice(invoice);
                      }}
                      title="Print Invoice"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    {/* Install Sheet */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openInstallSheet(invoice, 'Invoice');
                      }}
                      title="Print Install Sheet"
                      data-testid={`invoice-install-sheet-${invoice.id}`}
                    >
                      <ClipboardList className="w-4 h-4" />
                    </Button>
                    {/* Packing Slip */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openPackingSlip(invoice, 'Invoice');
                      }}
                      title="Print Packing Slip"
                      data-testid={`invoice-packing-slip-${invoice.id}`}
                    >
                      <Package className="w-4 h-4" />
                    </Button>
                    {/* Download PDF */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDownloadPDF(invoice.id, invoice.invoice_number);
                      }}
                      title="Download PDF"
                    >
                      <FileDown className="w-4 h-4" />
                    </Button>
                    {/* Copy Payment Link */}
                    {invoice.status !== 'paid' && invoice.balance_due > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const paymentUrl = `${window.location.origin}/pay/invoice/${invoice.id}`;
                          navigator.clipboard.writeText(paymentUrl);
                          toast.success('Payment link copied to clipboard!');
                        }}
                        title="Copy payment link"
                      >
                        <DollarSign className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Production/Fulfillment Tracking Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTrackingInvoice(invoice);
                        setShowTrackingModal(true);
                      }}
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      title="Update Production/Fulfillment"
                      data-testid="tracking-btn"
                    >
                      <Package className="w-4 h-4" />
                    </Button>
                    {/* Delete Invoice */}
                    <PermissionGate module="invoices" level="full">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        deleteInvoice(invoice.id);
                      }}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      title="Delete Invoice"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {formInvoice && (
        <InvoiceFormDialog token={token} invoice={formInvoice === 'new' ? null : formInvoice} onClose={() => setFormInvoice(null)} onSaved={() => fetchInvoices()} />
      )}

      {/* Production & Fulfillment Tracking Modal */}
      <AnimatePresence>
        {showTrackingModal && trackingInvoice && (
          <TrackingModal
            invoice={trackingInvoice}
            onClose={() => {
              setShowTrackingModal(false);
              setTrackingInvoice(null);
            }}
            onUpdateProduction={updateProductionStatus}
            onUpdateFulfillment={updateFulfillmentStatus}
          />
        )}
      </AnimatePresence>

      {/* Invoice Preview Modal */}
      <AnimatePresence>
        {selectedInvoice && (
          <InvoicePreviewModal
            invoice={selectedInvoice}
            onClose={() => setSelectedInvoice(null)}
            token={token}
            onPaymentAdded={handlePaymentAdded}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Invoice Preview Modal Component with Payments Tab
const InvoicePreviewModal = ({ invoice, onClose, token, onPaymentAdded }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [payments, setPayments] = useState(invoice?.payments || []);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'check',
    reference_number: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  if (!invoice) return null;

  const balanceDue = invoice.balance_due ?? (invoice.total - (invoice.amount_paid || 0));

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/invoices/${invoice.id}/payments`,
        {
          amount: parseFloat(paymentData.amount),
          payment_method: paymentData.payment_method,
          reference_number: paymentData.reference_number || null,
          notes: paymentData.notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment recorded successfully');
      setShowPaymentForm(false);
      setPaymentData({ amount: '', payment_method: 'check', reference_number: '', notes: '' });
      
      // Refresh payments
      const paymentsRes = await axios.get(
        `${API_URL}/admin/invoices/${invoice.id}/payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : paymentsRes.data.payments || []);
      
      // Notify parent to refresh
      if (onPaymentAdded) onPaymentAdded(invoice.id, response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Receipt className="w-5 h-5 text-blue-600" />
            <h2 className="font-semibold">Invoice {invoice.invoice_number}</h2>
            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusStyle(invoice.status)}`}>
              {getStatusLabel(invoice.status)}
            </span>
            <TestBadge doc={invoice} testId="invoice-detail-test-badge" />
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-200">
          <div className="px-6 flex gap-1">
            <button
              type="button"
              data-testid="invoice-modal-tab-details"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab('details');
              }}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details' 
                  ? 'border-amber-500 text-amber-600' 
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              Invoice Details
            </button>
            <button
              type="button"
              data-testid="invoice-modal-tab-payments"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab('payments');
              }}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'payments' 
                  ? 'border-amber-500 text-amber-600' 
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              <CreditCard className="w-4 h-4" />
              Payments ({payments.length})
            </button>
          </div>
        </div>

        {activeTab === 'details' ? (
          <div className="p-6">
            {/* Company Header */}
            <div className="bg-neutral-900 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-serif">{COMPANY_INFO.name}</h2>
                  <p className="text-neutral-400 mt-1">INVOICE</p>
                </div>
                <div className="text-right text-sm">
                  <p>{COMPANY_INFO.address}</p>
                  <p>{COMPANY_INFO.city}, {COMPANY_INFO.state} {COMPANY_INFO.zip}</p>
                  <p className="mt-2">{COMPANY_INFO.phone}</p>
                </div>
              </div>
            </div>

            {/* Invoice Info */}
            <div className="bg-blue-50 border-x border-blue-200 px-6 py-3 flex justify-between text-sm">
              <span><strong>Invoice #:</strong> {invoice.invoice_number}</span>
              <span><strong>Date:</strong> {new Date(invoice.created_at).toLocaleDateString()}</span>
              <span><strong>Due:</strong> {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'Upon Receipt'}</span>
            </div>

            {/* Bill To */}
            <div className="p-6 border-x border-b border-neutral-200 bg-white">
              <h3 className="text-xs font-medium text-neutral-500 uppercase mb-3">Bill To</h3>
              <div className="bg-neutral-50 p-4 rounded-lg inline-block">
                <p className="font-medium">{invoice.customer_name}</p>
                {invoice.company_name && <p className="text-neutral-600">{invoice.company_name}</p>}
                {formatAddress(invoice.billing_address) && (
                  <>
                    <p className="text-neutral-600 mt-2">{formatAddress(invoice.billing_address)}</p>
                    <p className="text-neutral-600">
                      {[invoice.billing_city, invoice.billing_state, invoice.billing_zip].filter(Boolean).join(', ')}
                    </p>
                  </>
                )}
                <p className="text-neutral-600 mt-2">{invoice.customer_email}</p>
              </div>
            </div>

            {/* Items */}
            <div className="p-6 border-x border-b border-neutral-200">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-xs font-medium text-neutral-500 uppercase">
                    <th className="pb-3">Description</th>
                    <th className="pb-3 text-center">Qty</th>
                    <th className="pb-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {isManual(invoice) ? <ManualLineRows items={invoice.items} /> : groupCartByRoom(invoice.items).map((group) => (
                    <React.Fragment key={group.room}>
                      <tr className="bg-stone-50" data-testid={`invoice-room-group-${group.room.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                        <td className="py-2 font-semibold" colSpan={2}>{group.room} <span className="text-xs font-normal text-neutral-400">· {group.quantity} {group.quantity === 1 ? 'shade' : 'shades'}</span></td>
                        <td className="py-2 text-right font-mono font-semibold">{formatPrice(group.subtotal)}</td>
                      </tr>
                      {group.items.map((item, idx) => (
                        <tr key={item.id || `item-${idx}`} className="border-b border-neutral-100">
                          <td className="py-3 pl-4">
                            <p className="font-medium">{item.windowLabel || item.config?.product_type?.replace('-', ' ')}</p>
                            <p className="text-sm text-neutral-500">
                              {item.windowLabel ? `${item.config?.product_type?.replace('-', ' ')} • ` : ''}{item.config?.width}" × {item.config?.height}" • {item.config?.fabric_material}
                            </p>
                          </td>
                          <td className="py-3 text-center">{item.quantity}</td>
                          <td className="py-3 text-right font-mono">{formatPrice(item.config?.total_price)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="mt-6 flex justify-end">
                <div className="w-64 space-y-2">
                  <DiscountRows inv={invoice} />
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Subtotal</span>
                    <span className="font-mono">{formatPrice(invoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Tax</span>
                    <span className="font-mono">{invoice.tax_details?.status === 'pending_address' ? 'TBC' : formatPrice(invoice.tax)}</span>
                  </div>
                  <TaxExplainLine doc={invoice} testId="invoice-detail-tax-explain" />
                  <NexusBanner doc={invoice} testId="invoice-nexus-banner" />
                  <div className="flex justify-between text-lg font-medium pt-2 border-t">
                    <span>Total</span>
                    <span className="font-mono">{formatPrice(invoice.total)}</span>
                  </div>
                  {(invoice.amount_paid || 0) > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Paid</span>
                      <span className="font-mono">-{formatPrice(invoice.amount_paid)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Balance Due</span>
                    <span className={`font-mono ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatPrice(balanceDue)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-neutral-50 rounded-b-lg text-center text-sm text-neutral-600">
              Thank you for your business! • {COMPANY_INFO.phone} • {COMPANY_INFO.email}
            </div>
            <InternalNotes invoice={invoice} token={token} onAdded={() => onPaymentAdded(invoice.id)} />
          </div>
        ) : (
          /* Payments Tab */
          <div className="p-6">
            {/* Payment Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-neutral-50 rounded-lg p-4 border">
                <div className="text-2xl font-bold">{formatPrice(invoice.total)}</div>
                <div className="text-sm text-neutral-500">Invoice Total</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="text-2xl font-bold text-green-600">{formatPrice(invoice.amount_paid || 0)}</div>
                <div className="text-sm text-green-700">Amount Paid</div>
              </div>
              <div className={`rounded-lg p-4 border ${balanceDue > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className={`text-2xl font-bold ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatPrice(balanceDue)}
                </div>
                <div className={`text-sm ${balanceDue > 0 ? 'text-red-700' : 'text-green-700'}`}>Balance Due</div>
              </div>
            </div>

            {/* Add Payment Button */}
            {balanceDue > 0 && !showPaymentForm && (
              <Button onClick={() => setShowPaymentForm(true)} className="mb-6" data-testid="invoice-record-payment-btn">
                <Plus className="w-4 h-4 mr-2" /> Record Payment
              </Button>
            )}

            {/* Payment Form */}
            {showPaymentForm && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6"
              >
                <h3 className="font-semibold mb-4">Record New Payment</h3>
                <form onSubmit={handleAddPayment} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Amount *</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={balanceDue}
                          value={paymentData.amount}
                          onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                          className="pl-7"
                          placeholder="0.00"
                          required
                          data-testid="payment-amount-input"
                        />
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">Max: {formatPrice(balanceDue)}</p>
                    </div>
                    <div>
                      <Label className="text-sm">Payment Method *</Label>
                      <select
                        value={paymentData.payment_method}
                        onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                        className="w-full mt-1 input-form"
                        required
                        data-testid="payment-method-select"
                      >
                        {PAYMENT_METHODS.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Reference/Check #</Label>
                      <Input
                        value={paymentData.reference_number}
                        onChange={(e) => setPaymentData({ ...paymentData, reference_number: e.target.value })}
                        className="mt-1"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Notes</Label>
                      <Input
                        value={paymentData.notes}
                        onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                        className="mt-1"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={submitting} data-testid="payment-submit-btn">
                      {submitting ? 'Recording...' : 'Record Payment'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowPaymentForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Payment History */}
            <h3 className="font-semibold mb-4">Payment History</h3>
            {payments.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 bg-neutral-50 rounded-lg">
                <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No payments recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {payments.map((payment, idx) => (
                  <div key={payment.id || idx} className="flex items-center justify-between p-4 bg-white border border-neutral-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{formatPrice(payment.amount)}</span>
                          <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs rounded-full">
                            {getPaymentMethodLabel(payment.payment_method)}
                          </span>
                        </div>
                        <p className="text-sm text-neutral-500">
                          {new Date(payment.created_at).toLocaleDateString()} 
                          {payment.reference_number && ` • Ref: ${payment.reference_number}`}
                        </p>
                        {payment.notes && <p className="text-xs text-neutral-400 mt-1">{payment.notes}</p>}
                      </div>
                    </div>
                    <div className="text-right text-xs text-neutral-400">
                      Recorded by {payment.recorded_by}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default InvoicesTab;
